/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Commissions Hooks - React Query for Commissions
 * ============================================================================
 *
 * Custom hooks for commission data fetching and mutations using React Query.
 *
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { commissionsService } from '@/services/superadmin/commissions.service';
import { Commission, CommissionSummary, CommissionStats, CommissionFilterParams, CreateCommissionRequest } from '@/types/superadmin.types';
import { ApiError, PaginatedResponse, PaginationParams } from '@/services/api/types';

// Query keys for cache management
export const commissionKeys = {
  all: ['commissions'] as const,
  lists: () => [...commissionKeys.all, 'list'] as const,
  list: (params?: PaginationParams & CommissionFilterParams) => [...commissionKeys.lists(), params] as const,
  details: () => [...commissionKeys.all, 'detail'] as const,
  detail: (id: string) => [...commissionKeys.details(), id] as const,
  partnerSummary: (partnerId: string) => [...commissionKeys.all, 'summary', partnerId] as const,
  stats: () => [...commissionKeys.all, 'stats'] as const,
};

/**
 * Hook to fetch paginated commissions list
 */
export function useCommissions(params?: PaginationParams & CommissionFilterParams) {
  return useQuery<PaginatedResponse<Commission>, ApiError>({
    queryKey: commissionKeys.list(params),
    queryFn: () => commissionsService.getCommissions(params),
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch single commission detail
 */
export function useCommission(id: string) {
  return useQuery<Commission, ApiError>({
    queryKey: commissionKeys.detail(id),
    queryFn: () => commissionsService.getCommission(id),
    enabled: !!id,
  });
}

/**
 * Hook to fetch commissions by partner
 */
export function usePartnerCommissions(partnerId: string, params?: PaginationParams) {
  return useQuery<PaginatedResponse<Commission>, ApiError>({
    queryKey: commissionKeys.list({ ...params, partnerId }),
    queryFn: () => commissionsService.getCommissionsByPartner(partnerId, params),
    enabled: !!partnerId,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch partner commission summary
 */
export function usePartnerCommissionSummary(partnerId: string) {
  return useQuery<CommissionSummary, ApiError>({
    queryKey: commissionKeys.partnerSummary(partnerId),
    queryFn: () => commissionsService.getPartnerSummary(partnerId),
    enabled: !!partnerId,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to fetch commission statistics
 */
export function useCommissionStats() {
  return useQuery<CommissionStats, ApiError>({
    queryKey: commissionKeys.stats(),
    queryFn: () => commissionsService.getStats(),
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to create a manual commission
 */
export function useCreateCommission() {
  const queryClient = useQueryClient();

  return useMutation<Commission, ApiError, CreateCommissionRequest>({
    mutationFn: (data) => commissionsService.createCommission(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commissionKeys.all });
    },
  });
}

/**
 * Hook to approve a commission
 */
export function useApproveCommission() {
  const queryClient = useQueryClient();

  return useMutation<Commission, ApiError, string>({
    mutationFn: (id) => commissionsService.approveCommission(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commissionKeys.all });
    },
  });
}

/**
 * Hook to bulk approve commissions
 */
export function useBulkApproveCommissions() {
  const queryClient = useQueryClient();

  return useMutation<{ approved: number }, ApiError, string[]>({
    mutationFn: (ids) => commissionsService.bulkApproveCommissions(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commissionKeys.all });
    },
  });
}

/**
 * Hook to auto-approve all pending commissions for a partner
 */
export function useAutoApprovePartnerCommissions() {
  const queryClient = useQueryClient();

  return useMutation<{ approved: number }, ApiError, string>({
    mutationFn: (partnerId) => commissionsService.autoApprovePartnerCommissions(partnerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commissionKeys.all });
    },
  });
}

/**
 * Hook to cancel a commission
 */
export function useCancelCommission() {
  const queryClient = useQueryClient();

  return useMutation<Commission, ApiError, { id: string; reason?: string }>({
    mutationFn: ({ id, reason }) => commissionsService.cancelCommission(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commissionKeys.all });
    },
  });
}

/**
 * Hook to reverse a paid commission
 */
export function useReverseCommission() {
  const queryClient = useQueryClient();

  return useMutation<Commission, ApiError, { id: string; reason?: string }>({
    mutationFn: ({ id, reason }) => commissionsService.reverseCommission(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commissionKeys.all });
    },
  });
}
